import React, {
    useState,
    createContext,
    useContext,
    useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
    requestPayerAuthentication,
    requestPayerAuthenticationCheckEnrollment,
    requestPayerAuthenticationServicePollStepUp
} from 'capi';
import emptyFunc from '../utils/emptyFunc';
import { logError } from '../utils/errorUtils';

export const POLLING_RESET = 'STOREFRONT_POLLING_RESET';

export const PayerAuthenticationContext = createContext({});

export const PayerAuthenticationProvider = ({ capi, children }) => {
    const [setupAccessToken, setSetupAccessToken] = useState(null);
    const [deviceDataCollectionUrl, setDeviceDataCollectionUrl] = useState(null);
    const [requiresPayerAuthentication, setRequiresPayerAuthentication] = useState(null);
    const [requiresStepUpAuthentication, setRequiresStepUpAuthentication] = useState(null);
    const [referenceId, setReferenceId] = useState(null);
    const [enrollmentAccessToken, setEnrollmentAccessToken] = useState(null);
    const [stepUpUrl, setStepUpUrl] = useState(null);
    const [pollingStatus, setPollingStatus] = useState(null);
    const [showStepUpOverlay, setShowStepUpOverlay] = useState(false);

    useEffect(() => {
        if (!requiresStepUpAuthentication) {
            setShowStepUpOverlay(false);
        }
    }, [requiresStepUpAuthentication]);

    const exitPayerAuthenticationFlow = () => {
        setRequiresPayerAuthentication(false);
        setRequiresStepUpAuthentication(false);
    };

    const requestPayerAuthenticationConfig = async (paymentData) => {
        try {
            setRequiresPayerAuthentication(null);
            const response = await requestPayerAuthentication(capi, paymentData);

            if (response.payerAuthSetupReply.securePaymentRequired) {
                setSetupAccessToken(response.payerAuthSetupReply.accessToken);
                setDeviceDataCollectionUrl(response.payerAuthSetupReply.deviceDataCollectionUrl);
                setReferenceId(response.payerAuthSetupReply.referenceId);
            }

            setRequiresPayerAuthentication(!!response.payerAuthSetupReply.securePaymentRequired);
        } catch (error) {
            logError(error);
            
            
            exitPayerAuthenticationFlow();
        }
    };

    const requestIfStepUpIsNeeded = async (requestData) => {
        try {
            const response = await requestPayerAuthenticationCheckEnrollment(capi, requestData);

            if (response.stepUpAuthenticationRequired) {
                setEnrollmentAccessToken(response.payerAuthEnrollReply.accessToken);
                setStepUpUrl(response.payerAuthEnrollReply.stepUpUrl);
                setRequiresStepUpAuthentication(true);
            } else {
                
                
                exitPayerAuthenticationFlow();
            }
        } catch (error) {
            logError(error);
            
            
            exitPayerAuthenticationFlow();
        }
    };

    const requestStepUpResults = async (requestData) => {
        try {
            setPollingStatus(POLLING_RESET);
            const response = await requestPayerAuthenticationServicePollStepUp(capi, requestData);

            if (response.stepUpStatus) {
                setPollingStatus(response.stepUpStatus);
            } else {
                
                
                exitPayerAuthenticationFlow();
            }
        } catch (error) {
            logError(error);
            
            
            exitPayerAuthenticationFlow();
        }
    };

    return (
        <PayerAuthenticationContext.Provider
            value={{
                setupAccessToken,
                requiresPayerAuthentication,
                deviceDataCollectionUrl,
                referenceId,
                requestPayerAuthenticationConfig,
                requestIfStepUpIsNeeded,
                setRequiresPayerAuthentication,
                requiresStepUpAuthentication,
                setRequiresStepUpAuthentication,
                enrollmentAccessToken,
                stepUpUrl,
                requestStepUpResults,
                pollingStatus,
                setPollingStatus,
                exitPayerAuthenticationFlow,
                showStepUpOverlay,
                setShowStepUpOverlay
            }}
        >
            {children}
        </PayerAuthenticationContext.Provider>
    );
};

PayerAuthenticationProvider.propTypes = {
    capi: PropTypes.func,
    children: PropTypes.node.isRequired
};

PayerAuthenticationProvider.defaultProps = {
    capi: emptyFunc()
};

export const usePayerAuthentication = () => useContext(PayerAuthenticationContext);
